import React, { useEffect } from "react";
import Layout from "../../../components/Layout";
import { Link, useLocation } from "react-router-dom";
import { FilterContextProvider } from "../../../contexts/FilterContext";
import Chip from "../../../components/Chip";
import DataTable from "../../../components/DataTable";
import useData from "../../../hooks/useData";
import { DeleteIcon } from "../../../assets/icons/Icons";
import SearchFilter from "../../../components/filters/SearchFilter";
import ViewSellOrder from "../../../components/sell-order/View";
import { formatDate } from "../../../utils/formatDate";
import { useTranslation } from "react-i18next";
import CustomTooltip from "../../../components/CustomTooltip";
import ConfirmModal from "../../../components/ConfirmModal";
import { useGlobalContext } from "../../../contexts/GlobalContext";
import FilterVoidCards from "../../../api/sell-orders/filterVoidCards";
import { DeleteVoidCard } from "../../../api/sell-orders/deleteVoidCard";
import DateRangeFilter from "../../../components/filters/DateRangeFilter";
import OfficesFilter from "../../../components/filters/OfficesFilter";
import ResetFilters from "../../../components/filters/ResetFilters";
import { useUserContext } from "../../../contexts/UserContext";
import ExportDataButton from "../../../components/ExportDataButton";
import { MOD_SELL_ORDER } from "../../../contstants";
import { downloadFile } from "../../../api/downloadFile";
import { ExportVoidOrders } from "../../../api/sell-orders/exportVoidOrders";
import SelectFilter from "../../../components/filters/SelectFilter";
import VoidCard from "../../../components/sell-order/VoidCard";
import { CentsToDollars } from "../../../utils/formatPrice";

function Index(props) {
  const { t } = useTranslation();

  const {
    data,
    errorMessage,
    loadingData,
    pageCount,
    selectedItem,
    showViewItem,
    setShowViewItem,
    showCreateItem,
    selectedForDelete,
    setSelectedForDelete,
    setShowCreateItem,
    showDeleteItem,
    setShowDeleteItem,
    deleteItem,
    deleteErrorMessage,
    setdeleteErrorMessage,
    setdeleteLoading,
    deleteLoading,
  } = useData(FilterVoidCards, DeleteVoidCard);

  const { can: userCan, hasPermission, hasDynamicPermissions } =
    useUserContext();

  const dynamic = hasDynamicPermissions();
  const canVoidItem = dynamic
    ? hasPermission("sell_orders_items", "write")
    : userCan(MOD_SELL_ORDER, "write");

  const { setPageTitle, setBreadcrumbs } = useGlobalContext();

  useEffect(() => {
    setPageTitle(t("Card Issuing"));
    setBreadcrumbs([t("Card Issuing"), t("Void Card")]);

    return () => {
      setPageTitle("");
      setBreadcrumbs([]);
    };
    // eslint-disable-next-line
  }, []);

  const handleExportCSV = async () => {
    try {
      const response = await FilterVoidCards(-1, queryParams);
      const ids = response.data.map((item) => item.sell_order_id);
      await downloadFile(ExportVoidOrders, { sell_order_id: ids }, "void_orders.csv");
    } catch (error) {
      console.error("Failed to download file:", error);
    }
  };

  // Export
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryParams = {};
  searchParams.forEach((value, key) => {
    queryParams[key] = value;
  });
  // map cards array from each data object
  const cardsData = data?.data || [];

  return (
    <Layout title={t("Void Card")} breadcrumbs={t("Card Issuing")}>
      {showCreateItem && (
        <VoidCard
          setShowCreateItem={setShowCreateItem}
          setShowDeleteItem={setShowDeleteItem}
          setSelectedForDelete={setSelectedForDelete}
          errorMessage={errorMessage}
          deleteErrorMessage={deleteErrorMessage}
        />
      )}

      {showViewItem && (
        <ViewSellOrder
          order={selectedItem}
          onClose={() => setShowViewItem(false)}
        />
      )}

      {showDeleteItem && (
        <ConfirmModal
          title={
            t("Card With ID ") + selectedForDelete["id"] ||
            selectedForDelete + t(" will be deleted")
          }
          question={t("Please confirm card deletion, this is irreversible!")}
          onClose={(e) => {
            setShowDeleteItem(false);
            setSelectedForDelete(null);
            setdeleteErrorMessage(null);
            setdeleteLoading(false);
          }}
          onConfirm={deleteItem}
          showDeleteItem={showDeleteItem}
          setShowCreateItem={setShowCreateItem}
          deleteErrorMessage={deleteErrorMessage}
          setdeleteLoading={setdeleteLoading}
          deleteLoading={deleteLoading}
          selectedForDelete={selectedForDelete}
          setSelectedForDelete={setSelectedForDelete}
          isOfficeRequired={true}
        />
      )}

      <DataTable
        filtersTop={
          <>
            <FilterContextProvider name="search_in_fields">
              <SelectFilter
                options={[
                  {
                    value: "prn",
                    label: t("By PRN"),
                  },
                  {
                    value: "user",
                    label: t("By user"),
                  },
                ]}
                defaultValue="prn"
                placeholder={<div>{t("Search by")}</div>}
              />
            </FilterContextProvider>
            <FilterContextProvider name="s">
              <SearchFilter placeholderText={t("Search")} />
            </FilterContextProvider>
          </>
        }
        filtersBottom={
          <>
            <FilterContextProvider name="date">
              <DateRangeFilter />
            </FilterContextProvider>
            <FilterContextProvider name="office_id">
              <OfficesFilter />
            </FilterContextProvider>
            <FilterContextProvider>
              <ResetFilters />
            </FilterContextProvider>
          </>
        }
        buttons={
          <>
            {canVoidItem && (
              <button
                className="btn btn-primary btn-medium"
                onClick={(e) => setShowCreateItem(true)}
              >
                {t("Void Card")}
              </button>
            )}
            <ExportDataButton
              className="btn btn-secondary btn-medium"
              onClick={handleExportCSV}
              label={t("Export CSV")}
              disabled={!data.data?.length}
            />
          </>
        }
        columns={[
          { header: t("Proxy"), accessor: "processor_reference" },
          { header: t("Order Number"), accessor: "sell_order_id" },
          { header: t("Order Type"), accessor: "sell_order_type" },
          { header: t("Date Voided"), accessor: "voided_at" },
          { header: t("Void Status"), accessor: "status" },
          { header: t("User"), accessor: "voided_by_name" },
          { header: t("User ID"), accessor: "voided_by" },
          { header: t("Original Load Amount"), accessor: "load_amount" },
          { header: t("Unload Amount"), accessor: "void_unload_amount" },
          { header: t("Office"), accessor: "office_name" },
        ]}
        rows={cardsData || []}
        pageCount={pageCount}
        loadingData={loadingData}
        error={errorMessage}
        actions={(item) =>
          (!dynamic || canVoidItem) && (
            <div className="d-flex align-items-center">
              <Link
                to="#"
                onClick={(e) => {
                  e.preventDefault();
                  setShowDeleteItem(true);
                  setSelectedForDelete(item.id);
                }}
              >
                <CustomTooltip icon={<DeleteIcon />} message={t("Void")} />
              </Link>
            </div>
          )
        }
        customOutputs={(columnName, value) => {
          switch (columnName) {
            case "status":
              let status = "inactive";
              if (
                value === "void_success" ||
                value === "success" ||
                value === "completed"
              ) {
                status = "active";
              }
              return (
                <Chip
                  state={status}
                  label={status.charAt(0).toUpperCase() + status.slice(1)}
                  showAsTooltop
                />
              );
            case "load_amount":
            case "void_unload_amount":
              return <p className='text-right'>{CentsToDollars(value)}</p>;
            case "voided_at":
              return formatDate(value);
            case "sell_order_type":
              return <p className="text-uppercase">{value}</p>;
            default:
              return value;
          }
        }}
      />
    </Layout>
  );
}

export default Index;
